<template>
    <div class="cms-document cms-rich-text text-base">
        <div ref="contentEl" @click="handleClick" v-html="document.mainHtml"></div>
        <slot></slot>
        <nav v-if="document.previousLink || document.nextLink" class="py-4 mt-8 flex gap-8 border-t">
            <NuxtLink
                v-if="document.previousLink"
                :to="document.previousLink.path"
                class="flex gap-2 items-baseline lg:max-w-1/2">
                <Icon name="arrow-left" />
                <span>{{ document.previousLink.title }}</span>
            </NuxtLink>
            <NuxtLink
                v-if="document.nextLink"
                :to="document.nextLink.path"
                class="flex gap-2 items-baseline ml-auto lg:max-w-1/2">
                <span class="text-right">{{ document.nextLink.title }}</span>
                <Icon name="arrow-right" />
            </NuxtLink>
        </nav>
    </div>
</template>
<script setup lang="ts">
const { document } = defineProps<{ document: CMSDocument }>();

const { handleClick } = useCmsLinkInterceptor();

const contentEl = ref<HTMLElement | null>(null);
let tableCleanups: (() => void)[] = [];

function setupTableScrollHints() {
    tableCleanups.forEach((fn) => fn());
    tableCleanups = [];

    if (!contentEl.value) return;

    for (const container of contentEl.value.querySelectorAll<HTMLElement>(".table-container")) {
        // Wrap in a relative-positioned parent so ::before/::after overlays can float above
        // the scroll container (inset box-shadow and background tricks paint below child content)
        const wrapper = globalThis.document.createElement("div");
        wrapper.className = "table-scroll-wrapper";
        container.parentNode!.insertBefore(wrapper, container);
        wrapper.appendChild(container);

        const update = () => {
            const maxScroll = container.scrollWidth - container.clientWidth;
            const overflows = maxScroll > 1;
            wrapper.toggleAttribute("data-tc-overflow", overflows);
            wrapper.toggleAttribute("data-tc-scrolled", overflows && container.scrollLeft > 1);
            wrapper.toggleAttribute("data-tc-end", overflows && container.scrollLeft >= maxScroll - 1);
        };

        // Convert vertical wheel events to horizontal scroll when the table has horizontal overflow.
        // This is especially helpful for tall tables where the scrollbar is far below the viewport.
        const onWheel = (e: WheelEvent) => {
            // Let native horizontal scroll (trackpad two-finger swipe) pass through
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
            const maxScroll = container.scrollWidth - container.clientWidth;
            if (maxScroll <= 0) return;
            // Normalize delta: browsers may report in pixels (0), lines (1), or pages (2)
            let delta = e.deltaY;
            if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
                delta *= 16; // ~1 line ≈ 16px
            } else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
                delta *= container.clientWidth;
            }
            const next = Math.max(0, Math.min(maxScroll, container.scrollLeft + delta));
            if (next === container.scrollLeft) return;
            container.scrollLeft = next;
            e.preventDefault();
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(container);
        container.addEventListener("scroll", update);
        container.addEventListener("wheel", onWheel, { passive: false });

        tableCleanups.push(() => {
            ro.disconnect();
            container.removeEventListener("scroll", update);
            container.removeEventListener("wheel", onWheel);
        });
    }
}

onMounted(setupTableScrollHints);
// Re-run after the DOM updates when the CMS document changes (SPA navigation reuses this component)
watch(() => document.mainHtml, setupTableScrollHints, { flush: "post" });
onUnmounted(() => tableCleanups.forEach((fn) => fn()));
</script>
<style>
.cms-document {
    min-width: 0;

    h1,
    h2,
    h3,
    h4 {
        scroll-margin-top: calc(var(--spacing) * 22);
        img {
            display: inline-block;
        }
    }

    h3 {
        margin: calc(var(--spacing) * 4) 0;
    }

    table {
        border-collapse: collapse;
        thead tr {
            background-color: var(--color-gray-200);
            text-align: left;
        }
        th,
        td {
            padding: calc(var(--spacing) * 2);
            border: 1px solid var(--color-gray-200);
        }
        tbody tr:nth-child(even) {
            background-color: var(--color-gray-100);
        }
    }

    code {
        color: var(--color-pink-500);
        font-size: 0.9em;
        background-color: var(--color-gray-100);
        padding: var(--spacing);
    }

    pre {
        overflow-x: auto;
        font-size: 0.9rem;
        background-color: var(--color-gray-100);
        padding: var(--spacing);
        code {
            background-color: transparent;
            padding: 0;
        }
    }

    time {
        display: block;
        margin-bottom: calc(var(--spacing) * 2);
        font-size: var(--text-sm);
        font-weight: bold;
    }

    .tags {
        display: flex;
        gap: calc(var(--spacing) * 2);
        flex-wrap: wrap;
    }

    .tag {
        margin: 0;
        padding: 0 calc(var(--spacing) * 2);
        background: var(--color-gray-200);
        border-radius: var(--radius-xl);
        font-size: var(--text-sm);
        border: 1px solid var(--color-gray-300);
    }

    .youtube-container {
        width: 100%;
        aspect-ratio: 16 / 9;
        margin-bottom: calc(var(--spacing) * 4);
    }

    .callout {
        padding: calc(var(--spacing) * 4);
        margin-bottom: calc(var(--spacing) * 4);
        padding-bottom: 0;
    }

    .callout-info {
        background-color: var(--color-primary-100);
        border: 1px solid var(--color-primary-300);
    }

    .callout-tip {
        background-color: var(--color-success-100);
        border: 1px solid var(--color-success-300);
    }

    .callout-warning {
        background-color: var(--color-warning-100);
        border: 1px solid var(--color-warning-300);
    }

    .post-image {
        margin: calc(var(--spacing) * 4) 0;
        img {
            width: 100%;
            object-fit: contain;
            max-height: 450px;
        }
    }

    .posts {
        font-size: var(--text-sm);
        h3 {
            margin-top: 0;
        }
        .post {
            display: grid;
            grid-template-columns: calc(var(--spacing) * 32) auto;
            gap: calc(var(--spacing) * 4);
            margin-bottom: calc(var(--spacing) * 8);

            @media (min-width: 768px) {
                grid-template-columns: calc(var(--spacing) * 48) auto;
            }
        }
        .post-image {
            margin: 0;
        }
    }

    .featured.posts .post {
        grid-template-columns: 1fr;
        h3 {
            font-size: var(--text-2xl);
        }

        @media (min-width: 1024px) {
            grid-template-columns: 1fr 1fr;
        }
    }

    .table-scroll-wrapper {
        position: relative;

        /* Gradient overlays float above the scroll container via ::before / ::after.
           They can't live on .table-container itself because inset box-shadows and
           background gradients paint below child elements with their own backgrounds. */
        &::before,
        &::after {
            content: "";
            position: absolute;
            inset-block: 0;
            width: 2rem;
            pointer-events: none;
            z-index: 1;
            opacity: 0;
            transition: opacity 0.2s ease;
        }

        &::before {
            left: 0;
            background: linear-gradient(to right, rgba(0, 0, 0, 0.25), transparent);
        }

        &::after {
            right: 0;
            background: linear-gradient(to left, rgba(0, 0, 0, 0.25), transparent);
        }

        /* At start with overflow — hint right edge only */
        &[data-tc-overflow]:not([data-tc-scrolled]) {
            &::after {
                opacity: 1;
            }
        }

        /* Scrolled to middle — hint both edges */
        &[data-tc-scrolled]:not([data-tc-end]) {
            &::before,
            &::after {
                opacity: 1;
            }
        }

        /* Reached the end — hint left edge only */
        &[data-tc-scrolled][data-tc-end] {
            &::before {
                opacity: 1;
            }
        }
    }

    .table-container {
        overflow-x: auto;
        max-width: 100%;
    }

    > div:first-child {
        display: flow-root;
    }

    .image-left {
        float: left;
        margin-right: 2rem;
        margin-bottom: 2rem;
    }

    .image-right {
        float: right;
        margin-left: 2rem;
        margin-bottom: 2rem;
    }
}
</style>
